import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  sessionsAPI, progressAPI, bookmarksAPI, notesAPI,
  strikethroughsAPI, timesAPI, reportsAPI
} from '../../services/api';
import ImageLightbox from '../Assessment/ImageLightbox';
import part1Questions from '../../data/questions/part1.json';
import './Quiz.css';

const allQuestionsMap = {};
[part1Questions].forEach((partData, idx) => {
  partData.questions.forEach(q => {
    allQuestionsMap[q.id] = { ...q, partNumber: idx + 1 };
  });
});

function Quiz() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);

  const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());
  const [strikethroughs, setStrikethroughs] = useState({});
  const [notes, setNotes] = useState({});
  const [showNotes, setShowNotes] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  const [timeRemaining, setTimeRemaining] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const timerRef = useRef(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportSubmitted, setReportSubmitted] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  const modalRef = useRef(null);

  useEffect(() => {
    async function loadSession() {
      try {
        const [sessionData, bookmarksData, strikethroughsData, notesData] = await Promise.all([
          sessionsAPI.get(sessionId),
          bookmarksAPI.getAll(),
          strikethroughsAPI.getAll(),
          notesAPI.getAll()
        ]);

        const sess = sessionData.session;
        setSession(sess);

        const qs = sess.questionIds.map(id => allQuestionsMap[id]).filter(Boolean);
        setQuestions(qs);
        setCurrentIndex(sess.current_index || 0);

        if (bookmarksData.bookmarks) {
          setBookmarkedQuestions(new Set(bookmarksData.bookmarks.map(b => b.question_id)));
        }
        setStrikethroughs(strikethroughsData.strikethroughs || {});

        const notesObj = {};
        (notesData.notes || []).forEach(n => {
          notesObj[n.question_id] = n.content;
        });
        setNotes(notesObj);

        const storedAnswers = localStorage.getItem(`quiz_answers_${sessionId}`);
        if (storedAnswers) {
          setAnswers(JSON.parse(storedAnswers));
        }

        if (sess.time_limit_seconds) {
          setTimeRemaining(sess.time_limit_seconds);
        }
        setQuestionStartTime(Date.now());

      } catch (err) {
        console.error('Failed to load session:', err);
        setError('Failed to load quiz session');
      } finally {
        setIsLoading(false);
      }
    }

    loadSession();
  }, [sessionId]);

  useEffect(() => {
    if (timeRemaining === null || showFeedback) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (session?.mode === 'test' && selectedAnswer !== null) {
            handleSubmit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timeRemaining, showFeedback, session?.mode]);

  useEffect(() => {
    const currentQuestion = questions[currentIndex];
    if (currentQuestion) {
      setNoteContent(notes[currentQuestion.id] || '');
    }
  }, [currentIndex, questions, notes]);

  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(`quiz_answers_${sessionId}`, JSON.stringify(answers));
    }
  }, [answers, sessionId]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const isTutorMode = session?.mode === 'tutor';

  const handleOptionClick = (optionIndex) => {
    if (showFeedback) return;
    setSelectedAnswer(optionIndex);
  };

  const handleStrikethrough = (e, optionIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (showFeedback) return;

    const questionId = currentQuestion.id;
    const currentStrikes = strikethroughs[questionId] || [];
    const isStruck = currentStrikes.includes(optionIndex);

    if (isStruck) {
      strikethroughsAPI.remove(questionId, optionIndex).catch(console.error);
      setStrikethroughs(prev => ({
        ...prev,
        [questionId]: currentStrikes.filter(i => i !== optionIndex)
      }));
    } else {
      strikethroughsAPI.add(questionId, optionIndex).catch(console.error);
      setStrikethroughs(prev => ({
        ...prev,
        [questionId]: [...currentStrikes, optionIndex]
      }));
    }
  };

  const handleSubmit = useCallback(async () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: selectedAnswer
    };
    setAnswers(newAnswers);

    if (questionStartTime) {
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
      timesAPI.save(currentQuestion.id, timeSpent).catch(console.error);
    }

    try {
      await progressAPI.saveAnswer(
        currentQuestion.partNumber,
        currentQuestion.id,
        selectedAnswer,
        isCorrect
      );
    } catch (err) {
      console.error('Failed to save answer:', err);
    }

    if (isTutorMode) {
      setShowFeedback(true);
    } else {
      if (currentIndex < totalQuestions - 1) {
        handleNextQuestion();
      } else {
        await completeQuiz();
      }
    }
  }, [selectedAnswer, currentQuestion, answers, questionStartTime, isTutorMode, currentIndex, totalQuestions]);

  const handleNextQuestion = async () => {
    if (currentIndex < totalQuestions - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setSelectedAnswer(answers[questions[nextIndex]?.id] ?? null);
      setShowFeedback(false);
      setShowNotes(false);

      if (session?.time_limit_seconds) {
        setTimeRemaining(session.time_limit_seconds);
      }
      setQuestionStartTime(Date.now());

      sessionsAPI.updateProgress(sessionId, nextIndex).catch(console.error);
    } else {
      await completeQuiz();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      setCurrentIndex(prevIndex);
      const prevQuestion = questions[prevIndex];
      const prevAnswer = answers[prevQuestion.id];

      if (prevAnswer !== undefined) {
        setSelectedAnswer(prevAnswer);
        if (isTutorMode) {
          setShowFeedback(true);
        }
      } else {
        setSelectedAnswer(null);
        setShowFeedback(false);
      }
      setShowNotes(false);

      if (session?.time_limit_seconds) {
        setTimeRemaining(session.time_limit_seconds);
      }
      setQuestionStartTime(Date.now());

      sessionsAPI.updateProgress(sessionId, prevIndex).catch(console.error);
    }
  };

  const completeQuiz = async () => {
    try {
      await sessionsAPI.complete(sessionId);
    } catch (err) {
      console.error('Failed to complete session:', err);
    }
    navigate(`/quiz-results/${sessionId}`);
  };

  const toggleBookmark = async () => {
    const questionId = currentQuestion.id;
    const isBookmarked = bookmarkedQuestions.has(questionId);

    try {
      if (isBookmarked) {
        await bookmarksAPI.remove(questionId);
        setBookmarkedQuestions(prev => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      } else {
        await bookmarksAPI.add(questionId, currentQuestion.partNumber);
        setBookmarkedQuestions(prev => new Set(prev).add(questionId));
      }
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  };

  const handleSaveNote = async () => {
    const questionId = currentQuestion.id;
    try {
      if (noteContent.trim()) {
        await notesAPI.save(questionId, noteContent);
        setNotes(prev => ({ ...prev, [questionId]: noteContent }));
      } else {
        await notesAPI.delete(questionId);
        setNotes(prev => {
          const next = { ...prev };
          delete next[questionId];
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const submitReport = async () => {
    try {
      await reportsAPI.submit(
        currentQuestion.id,
        currentQuestion.partNumber,
        currentQuestion.stem,
        reportComment
      );
      setReportSubmitted(prev => ({ ...prev, [currentQuestion.id]: true }));
      setShowReportModal(false);
      setReportComment('');
    } catch (err) {
      console.error('Failed to submit report:', err);
      alert('Failed to submit report. Please try again.');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return <div className="loading">Loading quiz...</div>;
  }

  if (error || !currentQuestion) {
    return (
      <div className="quiz">
        <div className="error-message">{error || 'Question not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  const isCorrect = showFeedback && selectedAnswer === currentQuestion.correctAnswer;
  const optionLetters = ['A', 'B', 'C', 'D'];
  const questionStrikes = strikethroughs[currentQuestion.id] || [];

  return (
    <div className="quiz">
      <div className="quiz-header">
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Exit Quiz
        </button>
        <div className="quiz-info">
          <span className="mode-badge">{isTutorMode ? 'Tutor' : 'Test'} Mode</span>
          {timeRemaining !== null && (
            <span className={`timer ${timeRemaining <= 10 ? 'warning' : ''}`}>
              {formatTime(timeRemaining)}
            </span>
          )}
        </div>
      </div>

      <div className="quiz-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
          />
        </div>
        <p>Question {currentIndex + 1} of {totalQuestions}</p>
      </div>

      <div className="question-card">
        <div className="question-header-row">
          <div className="question-number">Question {currentIndex + 1}</div>
          <div className="question-actions">
            <button
              className={`action-btn note-btn ${notes[currentQuestion.id] ? 'has-note' : ''}`}
              onClick={() => setShowNotes(!showNotes)}
              title="Notes"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
              </svg>
            </button>
            <button
              className={`action-btn bookmark-btn ${bookmarkedQuestions.has(currentQuestion.id) ? 'active' : ''}`}
              onClick={toggleBookmark}
              title={bookmarkedQuestions.has(currentQuestion.id) ? 'Remove bookmark' : 'Bookmark'}
            >
              {bookmarkedQuestions.has(currentQuestion.id) ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="question-stem">{currentQuestion.stem}</div>

        {currentQuestion.images && currentQuestion.images.length > 0 && (
          <div className="question-images">
            {currentQuestion.images.map((image, idx) => (
              <figure key={idx} className="question-image-container">
                <img
                  src={image.url}
                  alt={image.alt || `Question image ${idx + 1}`}
                  className="question-image"
                  onClick={() => setLightboxImage(image)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setLightboxImage(image)}
                />
                {image.caption && (
                  <figcaption className="question-image-caption">{image.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        <div className="options" role="radiogroup" aria-label="Answer options">
          {currentQuestion.options.map((option, index) => {
            let className = 'option';
            const isSelected = index === selectedAnswer;
            const isCorrectAnswer = index === currentQuestion.correctAnswer;
            const isIncorrect = showFeedback && isSelected && !isCorrectAnswer;
            const isStruck = questionStrikes.includes(index);

            if (showFeedback) {
              className += ' disabled';
              if (isCorrectAnswer) className += ' correct';
              if (isIncorrect) className += ' incorrect';
            } else if (isSelected) {
              className += ' selected';
            }
            if (isStruck) className += ' strikethrough';

            return (
              <button
                key={index}
                type="button"
                className={className}
                onClick={() => handleOptionClick(index)}
                onContextMenu={(e) => handleStrikethrough(e, index)}
                disabled={showFeedback}
                role="radio"
                aria-checked={isSelected}
                title="Right-click to strikethrough"
              >
                <span className="option-letter">{optionLetters[index]}.</span>
                <span className="option-text">{option}</span>
                {!showFeedback && (
                  <button
                    className={`strike-btn ${isStruck ? 'struck' : ''}`}
                    onClick={(e) => handleStrikethrough(e, index)}
                    title="Toggle strikethrough"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="4" y1="12" x2="20" y2="12"/>
                    </svg>
                  </button>
                )}
              </button>
            );
          })}
        </div>

        {showNotes && (
          <div className="notes-panel">
            <h4>Personal Notes</h4>
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              onBlur={handleSaveNote}
              placeholder="Add your notes here..."
              rows={3}
            />
            <div className="notes-meta">
              <span>{noteContent.length}/10000</span>
              <span>Auto-saved on blur</span>
            </div>
          </div>
        )}
      </div>

      {!showFeedback && (
        <button
          className="btn btn-primary submit-btn"
          onClick={handleSubmit}
          disabled={selectedAnswer === null}
        >
          {isTutorMode ? 'Submit Answer' : (currentIndex < totalQuestions - 1 ? 'Next' : 'Finish')}
        </button>
      )}

      {showFeedback && (
        <div className={`feedback-panel ${isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
          <div className="feedback-header">
            <span className="feedback-icon">{isCorrect ? '✓' : '✗'}</span>
            <strong>{isCorrect ? 'Correct!' : 'Incorrect'}</strong>
          </div>

          <div className="rationale">
            <strong>Explanation:</strong> {currentQuestion.rationale}
          </div>

          <div className="source-excerpt">
            <h4>Source Material</h4>
            <blockquote>"{currentQuestion.sourceExcerpt}"</blockquote>
            <cite>— {currentQuestion.sourceLocation}</cite>
          </div>

          <div className="report-question">
            {reportSubmitted[currentQuestion.id] ? (
              <span className="report-submitted">Thank you for your feedback</span>
            ) : (
              <button className="btn-report" onClick={() => setShowReportModal(true)}>
                <span>👎</span> Report Issue
              </button>
            )}
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div
            ref={modalRef}
            className="modal-content"
            onClick={e => e.stopPropagation()}
          >
            <h3>Report Question Issue</h3>
            <p className="modal-question-id">Question: {currentQuestion.id}</p>
            <textarea
              placeholder="Describe the issue..."
              value={reportComment}
              onChange={e => setReportComment(e.target.value)}
              rows={4}
            />
            <div className="modal-buttons">
              <button className="btn btn-secondary" onClick={() => setShowReportModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submitReport}>
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="navigation-buttons">
          <button
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            ← Previous
          </button>
          <button className="btn btn-primary" onClick={handleNextQuestion}>
            {currentIndex < totalQuestions - 1 ? 'Next Question →' : 'Finish Quiz'}
          </button>
        </div>
      )}

      {!isTutorMode && !showFeedback && (
        <div className="navigation-buttons">
          <button
            className="btn btn-secondary"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            ← Previous
          </button>
          <div className="skip-text">
            {selectedAnswer === null && 'Select an answer'}
          </div>
        </div>
      )}

      {lightboxImage && (
        <ImageLightbox
          image={lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}

export default Quiz;
